<template><div><code-well-header>
  <dt-stack gap="500" align="center">
    <dt-stack
      gap="500"
      class="d-bgc-moderate-opaque d-bar8"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 1
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 2
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 3
      </div>
    </dt-stack>
    <dt-stack
      gap="500"
      direction="row"
      class="d-bgc-moderate-opaque d-bar8"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 1
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 2
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Stack item 3
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<h2 id="direction" tabindex="-1" class="d-docsite--header-2">Direction <a class="header-anchor d-link d-docsite--link d-link" href="#direction" aria-hidden="true">#</a></h2>
<h3 id="column" tabindex="-1" class="d-docsite--header-3">Column <a class="header-anchor d-link d-docsite--link d-link" href="#column" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>direction=&quot;column&quot;</code> will flow child items vertically, i.e. top to bottom. It is the default direction and doesn't need to be explictily set.</p>
<code-well-header>
  <dt-stack
    gap="500"
    class="d-bgc-moderate-opaque d-bar8"
    ref="columnExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.columnExample"
vueCode='
<dt-stack
  gap="500"
>
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>
'
/>
<h3 id="row" tabindex="-1" class="d-docsite--header-3">Row <a class="header-anchor d-link d-docsite--link d-link" href="#row" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>direction=&quot;row&quot;</code> will flow child items horizontally, i.e. left to right.</p>
<code-well-header>
  <dt-stack
    gap="500"
    direction="row"
    class="d-bgc-moderate-opaque d-bar8"
    ref="rowExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.rowExample"
vueCode='
<dt-stack
  gap="500"
  direction="row"
>
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>
'
showHtmlWarning />
<h3 id="row-reverse" tabindex="-1" class="d-docsite--header-3">Row Reverse <a class="header-anchor d-link d-docsite--link d-link" href="#row-reverse" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack
    gap="500"
    direction="row-reverse"
    class="d-bgc-moderate-opaque d-bar8"
    ref="rowReverseExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.rowReverseExample"
vueCode='
<dt-stack
  gap="500"
  direction="row-reverse"
>
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>'
/>
<h3 id="column-reverse" tabindex="-1" class="d-docsite--header-3">Column Reverse <a class="header-anchor d-link d-docsite--link d-link" href="#column-reverse" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack
    gap="500"
    direction="column-reverse"
    class="d-bgc-moderate-opaque d-bar8"
    ref="columnReverseExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.columnReverseExample"
vueCode='
<dt-stack
  gap="500"
  direction="column-reverse"
>
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>
'
/>
<h2 id="as" tabindex="-1" class="d-docsite--header-2">As <a class="header-anchor d-link d-docsite--link d-link" href="#as" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>as</code> prop controls which HTML element the Stack component renders as. Defaults to <code v-pre>&lt;div&gt;</code>, but can be declared as any valid HTML element to ensure semantic and accessible markup.</p>
<p class="d-docsite--paragraph">Declaring as an appropriate HTML element improves accessibility by helping screen readers better understand document structure, and maintainability with clear code intent.</p>
<p class="d-docsite--paragraph"><strong>Common <code v-pre>as</code> values:</strong> <code v-pre>span</code>, <code v-pre>ul</code>, <code v-pre>ol</code>, <code v-pre>li</code>, <code v-pre>section</code>, <code v-pre>nav</code>, <code v-pre>article</code>, <code v-pre>main</code>, <code v-pre>aside</code>, <code v-pre>header</code>, <code v-pre>footer</code>.</p>
<h3 id="example-section" tabindex="-1" class="d-docsite--header-3">Example: section <a class="header-anchor d-link d-docsite--link d-link" href="#example-section" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>as=&quot;section&quot;</code> to create a thematic grouping of content.</p>
<code-well-header>
  <dt-stack
    as="section"
    gap="400"
    class="d-bgc-moderate-opaque d-bar8"
    ref="asSectionExample"
  >
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 1</div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 2</div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 3</div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.asSectionExample"
vueCode='
<dt-stack
  as="section"
  gap="400"
>
  <div>Stack item 1</div>
  <div>Stack item 2</div>
  <div>Stack item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="example-span" tabindex="-1" class="d-docsite--header-3">Example: span <a class="header-anchor d-link d-docsite--link d-link" href="#example-span" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>as=&quot;span&quot;</code> when you need an inline container.</p>
<code-well-header>
  <dt-stack
    as="span"
    direction="row"
    gap="300"
    class="d-bgc-moderate-opaque d-bar8"
    ref="asSpanExample"
  >
    <span class="d-bgc-moderate-opaque d-bar8 d-p16">Inline item 1</span>
    <span class="d-bgc-moderate-opaque d-bar8 d-p16">Inline item 2<br>with a second line</span>
    <span class="d-bgc-moderate-opaque d-bar8 d-p16">Inline item 3</span>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.asSpanExample"
vueCode='
<dt-stack
  as="span"
  direction="row"
  gap="300"
>
  <span>Inline item 1</span>
  <span>Inline item 2</span>
  <span>Inline item 3</span>
</dt-stack>
'
showHtmlWarning />
<h2 id="gap" tabindex="-1" class="d-docsite--header-2">Gap <a class="header-anchor d-link d-docsite--link d-link" href="#gap" aria-hidden="true">#</a></h2>
<code-well-header>
  <dt-stack gap="500" class="d-w100p">
    <dt-stack gap="200">
      <h3 class="d-label" id="interactive-gap-label">Select a gap option</h3>
      <dt-stack
        :direction="{ 'default': 'column', 'md': 'row' }"
        gap="200"
        class="d-ba d-bc-subtle d-p2 d-bar8"
        role="group"
        aria-labelledby="interactive-gap-label"
        @keydown="handleGapButtonKeydown"
      >
        <dt-button
          v-for="(gap, index) in gaps"
          :ref="el => gapButtonRefs[index] = el"
          size="xs"
          kind="muted"
          importance="clear"
          class="d-fl1 d-bar6"
          :key="gap"
          :active="gap === selectedGap"
          @click="setGap(gap)"
          :tabindex="gap === selectedGap ? 0 : -1"
          @focus="focusedGapIndex = index"
        >
          {{ gap }}
        </dt-button>
      </dt-stack>
    </dt-stack>
    <dt-stack
      :direction="{ 'default': 'column', 'md': 'row' }"
      gap="500"
      class="d-w100p"
      align="start"
    >
      <dt-stack class="d-w100p md:d-w50p" gap="300">
        <h3 class="d-headline--md">Column</h3>
        <dt-stack
          :gap="selectedGap"
          ref="gapExample"
          class="d-bgc-moderate-opaque d-t d-td300 d-bar8 d-ttf-quint"
        >
          <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 1</div>
          <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 2</div>
          <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 3</div>
        </dt-stack>
      </dt-stack>
      <dt-stack class="d-w100p md:d-w50p" gap="300">
        <h3 class="d-headline--md">Row</h3>
        <dt-stack
          direction="row"
          :gap="selectedGap"
          ref="gapExample"
          class="d-bgc-moderate-opaque d-t d-td300 d-bar8 d-ttf-quint"
        >
          <div class="d-bgc-moderate-opaque d-bar8 d-p16 d-fl1">Stack item 1</div>
          <div class="d-bgc-moderate-opaque d-bar8 d-p16 d-fl1">Stack item 2</div>
          <div class="d-bgc-moderate-opaque d-bar8 d-p16 d-fl1">Stack item 3</div>
        </dt-stack>
      </dt-stack>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.gapExample"
vueCode='
<dt-stack
  gap="400"
>
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>
'
/>
<h3 id="available-gaps" tabindex="-1" class="d-docsite--header-3">Available gaps <a class="header-anchor d-link d-docsite--link d-link" href="#available-gaps" aria-hidden="true">#</a></h3>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0">
            <div class="d-p16 d-bb d-bc-default d-bbw1">Size</div>
          </th>
          <th scope="col" class="d-p0 d-bbw0">
            <div class="d-p16 d-bb d-bc-default d-bbw1">Design Token</div>
          </th>
          <th scope="col" class="d-ta-right d-p0 d-bbw0">
            <div class="d-p16 d-bb d-bc-default d-bbw1">REM</div>
          </th>
          <th scope="col" class="d-ta-right d-p0 d-bbw0">
            <div class="d-p16 d-bb d-bc-default d-bbw1">PX</div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
              0
          </th>
          <td>
            <strong>var(--dt-space-0)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.0rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             0px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
             50
          </th>
          <td>
            <strong>var(--dt-space-50)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.05rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            .5px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            100
          </th>
          <td>
            <strong>var(--dt-space-100)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.1rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             1px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            200
          </th>
          <td>
            <strong>var(--dt-space-200)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.2rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             2px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            300
          </th>
          <td>
            <strong>var(--dt-space-300)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.4rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             4px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            350
          </th>
          <td>
            <strong>var(--dt-space-350)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.6rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             6px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            400
          </th>
          <td>
            <strong>var(--dt-space-400)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            0.8rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
             8px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            450
          </th>
          <td>
            <strong>var(--dt-space-450)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            1.2rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            12px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            500
          </th>
          <td>
            <strong>var(--dt-space-500)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            1.6rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            16px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            525
          </th>
          <td>
            <strong>var(--dt-space-525)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            2.0rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            20px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            550
          </th>
          <td>
            <strong>var(--dt-space-550)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            2.4rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            24px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            600
          </th>
          <td>
            <strong>var(--dt-space-600)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            3.2rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            32px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            625
          </th>
          <td>
            <strong>var(--dt-space-625)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            4.2rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            42px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            650
          </th>
          <td>
            <strong>var(--dt-space-650)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            4.8rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            48px
          </td>
        </tr>
        <tr>
          <th scope="row" class="d-code--sm d-docsite-code">
            700
          </th>
          <td>
            <strong>var(--dt-space-700)</strong>
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            6.4rem
          </td>
          <td class="d-code--sm d-docsite-code d-ta-right">
            64px
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h2 id="align" tabindex="-1" class="d-docsite--header-2">Align <a class="header-anchor d-link d-docsite--link d-link" href="#align" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>align</code> prop controls how items are aligned along the cross-axis (perpendicular to the stack direction). For row stacks, this controls vertical alignment. For column stacks, this controls horizontal alignment.</p>
<p class="d-docsite--paragraph">Available <code v-pre>align</code> values: <code v-pre>start</code>, <code v-pre>center</code>, <code v-pre>end</code>, <code v-pre>stretch</code>, <code v-pre>baseline</code>.</p>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The <code v-pre>align</code> prop is optional. Unless specified, it will default vertical stacks to <code v-pre>align-items=&quot;stretch&quot;</code> and horizontal stacks to <code v-pre>align-items=&quot;center&quot;</code>.</p>
<code-well-header>
  <dt-stack
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="alignDefaultExample"
  >
    <dt-stack
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-stretch"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
    <dt-stack
      direction="row"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignDefaultExample"
vueCode='
<dt-stack>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
<dt-stack direction="row">
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="start" tabindex="-1" class="d-docsite--header-3">Start <a class="header-anchor d-link d-docsite--link d-link" href="#start" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the start of the cross-axis.</p>
<code-well-header>
  <dt-stack
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="alignStartExample"
  >
    <dt-stack
      gap="400"
      align="start"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-start"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      align="start"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-start"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignStartExample"
vueCode='
<dt-stack
  gap="400"
  align="start"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
<dt-stack
  direction="row"
  gap="400"
  align="start"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="center" tabindex="-1" class="d-docsite--header-3">Center <a class="header-anchor d-link d-docsite--link d-link" href="#center" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Center items along the cross-axis.</p>
<code-well-header>
  <dt-stack
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="alignCenterExample"
  >
    <dt-stack
      gap="400"
      align="center"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      align="center"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignCenterExample"
vueCode='
<dt-stack
  direction="row"
  gap="400"
  align="center"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="end" tabindex="-1" class="d-docsite--header-3">End <a class="header-anchor d-link d-docsite--link d-link" href="#end" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the end of the cross-axis.</p>
<code-well-header>
  <dt-stack
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="alignEndExample"
  >
    <dt-stack
      gap="400"
      align="end"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-end"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      align="end"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-end"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignEndExample"
vueCode='
<dt-stack
  gap="400"
  align="end"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
<dt-stack
  direction="row"
  gap="400"
  align="end"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="stretch" tabindex="-1" class="d-docsite--header-3">Stretch <a class="header-anchor d-link d-docsite--link d-link" href="#stretch" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stretch items to fill the container height.</p>
<code-well-header>
  <dt-stack
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="alignStretchExample"
  >
    <dt-stack
      gap="400"
      align="stretch"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-stretch"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      align="stretch"
      class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-stretch"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Taller item<br>
        with more content
      </div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">
        Short
      </div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignStretchExample"
vueCode='
<dt-stack
  direction="row"
  gap="400"
  align="stretch"
>
  <div>Short</div>
  <div>
    Taller item<br>
    with more content
  </div>
  <div>Short</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="baseline" tabindex="-1" class="d-docsite--header-3">Baseline <a class="header-anchor d-link d-docsite--link d-link" href="#baseline" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items along their text baselines.</p>
<code-well-header>
  <dt-stack
    direction="row"
    gap="400"
    align="baseline"
    class="d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--baseline"
    ref="alignBaselineExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8 d-body--sm">
      Small body
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8 d-body--md">
      Medium body
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8 d-headline--xxl">
      Large headline
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.alignBaselineExample"
vueCode='
<dt-stack
  direction="row"
  gap="400"
  align="baseline"
>
  <div class="d-body--sm">Small text</div>
  <div class="d-body--md">Medium text</div>
  <div class="d-headline--md">Large text</div>
</dt-stack>
'
showHtmlWarning />
<h2 id="justify" tabindex="-1" class="d-docsite--header-2">Justify <a class="header-anchor d-link d-docsite--link d-link" href="#justify" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The <code v-pre>justify</code> prop controls how items are distributed along the main axis (the direction of the stack). For row stacks, this controls horizontal distribution. For column stacks, this controls vertical distribution.</p>
<p class="d-docsite--paragraph">Available <code v-pre>justify</code> values: <code v-pre>start</code> (default), <code v-pre>center</code>, <code v-pre>end</code>, <code v-pre>space-around</code>, <code v-pre>space-between</code>, <code v-pre>space-evenly</code>.</p>
<h3 id="start-1" tabindex="-1" class="d-docsite--header-3">Start <a class="header-anchor d-link d-docsite--link d-link" href="#start-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the start of the main axis (default).</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    align="stretch"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyStartExample"
  >
    <dt-stack
      gap="400"
      justify="start"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-start"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="start"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-start"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyStartExample"
vueCode='
<dt-stack
  justify="start"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="center-1" tabindex="-1" class="d-docsite--header-3">Center <a class="header-anchor d-link d-docsite--link d-link" href="#center-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Center items along the main axis.</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyCenterExample"
  >
    <dt-stack
      gap="400"
      justify="center"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="center"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyCenterExample"
vueCode='
<dt-stack
  justify="center"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="end-1" tabindex="-1" class="d-docsite--header-3">End <a class="header-anchor d-link d-docsite--link d-link" href="#end-1" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Align items to the end of the main axis.</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyEndExample"
  >
    <dt-stack
      gap="400"
      justify="end"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-end"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="end"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-end"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyEndExample"
vueCode='
<dt-stack
  justify="end"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="space-around" tabindex="-1" class="d-docsite--header-3">Space Around <a class="header-anchor d-link d-docsite--link d-link" href="#space-around" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with equal space around each item.</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyAroundExample"
  >
    <dt-stack
      gap="400"
      justify="space-around"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="space-around"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyAroundExample"
vueCode='
<dt-stack
  justify="space-around"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="space-between" tabindex="-1" class="d-docsite--header-3">Space Between <a class="header-anchor d-link d-docsite--link d-link" href="#space-between" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with space between them, edges flush to container.</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyBetweenExample"
  >
    <dt-stack
      gap="400"
      justify="space-between"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="space-between"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyBetweenExample"
vueCode='
<dt-stack
  direction="row"
  gap="400"
  justify="space-between"
  class="d-w100p"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h3 id="space-evenly" tabindex="-1" class="d-docsite--header-3">Space Evenly <a class="header-anchor d-link d-docsite--link d-link" href="#space-evenly" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Distribute items with equal space between all items, including edges.</p>
<code-well-header>
  <dt-stack
    class="d-w100p"
    gap="500"
    :direction="{ default: `column`, md: `row` }"
    ref="justifyEvenlyExample"
  >
    <dt-stack
      gap="400"
      justify="space-evenly"
      class="d-w100p d-h332 d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--block-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
    <dt-stack
      direction="row"
      gap="400"
      justify="space-evenly"
      class="d-w100p d-bgc-moderate-opaque d-bar8 axis-outline axis-outline--inline-center"
    >
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
      <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.justifyEvenlyExample"
vueCode='
<dt-stack
  justify="space-evenly"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
showHtmlWarning />
<h2 id="responsive" tabindex="-1" class="d-docsite--header-2">Responsive <a class="header-anchor d-link d-docsite--link d-link" href="#responsive" aria-hidden="true">#</a></h2>
<h3 id="example" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stacks column at small screen size and column reverse at large screen</p>
<code-well-header>
  <dt-stack
    :direction="{ default: `row`, sm: `column`, lg: `column-reverse` }"
    gap="500"
    class="d-bgc-moderate-opaque"
    ref="responsiveDirectionExample"
  >
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.responsiveDirectionExample"
vueCode='
<dt-stack
  :direction="{ `default`: `row`, `sm`: `column`, `lg`: `column-reverse` }"
  gap="500"
>
  <div>
    Stack item 1
  </div>
  <div>
    Stack item 2
  </div>
  <div>
    Stack item 3
  </div>
</dt-stack>
'
showHtmlWarning />
<p class="d-docsite--paragraph">Set <code v-pre>200</code> as the default gap, <code v-pre>300</code> for small and larger, <code v-pre>400</code> for medium, <code v-pre>500</code> for large, and <code v-pre>600</code> for extra large. Learn more about how our breakpoints work in the <RouteLink to="/utilities/responsive/breakpoints.html" class="d-docsite--link d-link">Responsive Breakpoints documentation</RouteLink>.</p>
<code-well-header>
  <dt-stack
    :gap="{ default: '200', xl: '600', lg: '500', md: '400', sm: '300' }"
    class="d-bgc-moderate-opaque"
    ref="responsiveGapExample"
  >
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 1
    </div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 2
    </div>
    <div class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 3
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.responsiveGapExample"
vueCode='
<dt-stack :gap="{ default: `300`, xl: `600`, lg: `500`, md: `400`, sm: `300` }">
  <div> Stack item 1 </div>
  <div> Stack item 2 </div>
  <div> Stack item 3 </div>
</dt-stack>
'
/>
<h3 id="nested-example" tabindex="-1" class="d-docsite--header-3">Nested Example <a class="header-anchor d-link d-docsite--link d-link" href="#nested-example" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Stacks row with gap 500 and stacks in row reverse the nested stack with gap 500.</p>
<code-well-header>
  <dt-stack
    direction="row"
    as="section"
    gap="500"
    ref="nestedStackExample"
    class="d-bgc-moderate-opaque d-bar8"
  >
    <dt-stack class="d-bgc-moderate-opaque d-bar8 d-p16">
      Stack item 1
    </dt-stack>
    <dt-stack gap="500" class="d-bgc-moderate-opaque">
      <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 2</div>
      <dt-stack
        direction="row-reverse"
        gap="500"
      >
        <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 3<br>with multiple lines</div>
        <div class="d-bgc-moderate-opaque d-bar8 d-p16">Stack item 4</div>
      </dt-stack>
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.nestedStackExample"
vueCode='
<dt-stack
  direction="row"
  as="section"
  gap="500"
>
  <dt-stack>
    Stack item 1
  </dt-stack>
  <dt-stack gap="500">
    <div>Stack item 2</div>
    <dt-stack
      direction="row-reverse"
      gap="500"
    >
      <div>Stack item 3<br>with multiple lines</div>
      <div>Stack item 4</div>
    </dt-stack>
  </dt-stack>
</dt-stack>
'
showHtmlWarning />
<h3 id="example-align-and-justify" tabindex="-1" class="d-docsite--header-3">Example: Align and Justify <a class="header-anchor d-link d-docsite--link d-link" href="#example-align-and-justify" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Like <code v-pre>direction</code> and <code v-pre>gap</code>, the <code v-pre>align</code> and <code v-pre>justify</code> props support responsive object syntax to change alignment at different breakpoints.</p>
<code-well-header>
  <dt-stack
    direction="row"
    gap="400"
    :align="{ default: 'start', md: 'center', lg: 'end' }"
    class="d-bgc-moderate-opaque d-bar8"
    ref="responsiveAlignExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Short
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Taller<br>
      item
    </div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">
      Short
    </div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.responsiveAlignExample"
vueCode='
<dt-stack
  direction="row"
  gap="400"
  :align="{ default: `start`, md: `center`, lg: `end` }"
>
  <div>Short</div>
  <div>
    Taller<br>
    item
  </div>
  <div>Short</div>
</dt-stack>
'
/>
<p class="d-docsite--paragraph">Resize your browser to see the alignment change at different breakpoints.</p>
<code-well-header>
  <dt-stack
    direction="row"
    gap="0"
    :justify="{ default: 'start', md: 'center', lg: 'space-between' }"
    class="d-w100p d-bgc-moderate-opaque d-bar8"
    ref="responsiveJustifyExample"
  >
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 1</div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 2</div>
    <div class="d-bgc-moderate-opaque d-p16 d-bar8">Item 3</div>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs.responsiveJustifyExample"
vueCode='
<dt-stack
  direction="row"
  :justify="{ default: `start`, md: `center`, lg: `space-between` }"
  class="d-w100p"
>
  <div>Item 1</div>
  <div>Item 2</div>
  <div>Item 3</div>
</dt-stack>
'
/>
<p class="d-docsite--paragraph">Resize your browser to see the justification change at different breakpoints.</p>
<h2 id="migrating-from-flex-css-utilities" tabindex="-1" class="d-docsite--header-2">Migrating from Flex CSS Utilities <a class="header-anchor d-link d-docsite--link d-link" href="#migrating-from-flex-css-utilities" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">View the <a href="/about/whats-new/posts/2025-12-2" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Migrating from Flex CSS Utilities to DtStack</a> for more details.</p>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="stack" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="stack"></component-class-table></div></template>


<script setup>
  import { ref, nextTick } from 'vue';
  import {
    DtIconMessage,
    DtIconLock,
  } from '@dialpad/dialtone-icons/vue3';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';

  const selectedGap = ref('400');
  const focusedGapIndex = ref(0);
  const gapButtonRefs = ref([]);

  const setGap = (gap) => {
    selectedGap.value = gap;
  };

  const gaps = window.DIALTONE_CONSTANTS.DT_STACK_GAP;

  // Find the index of the currently selected gap
  const getSelectedGapIndex = () => {
    return gaps.indexOf(selectedGap.value);
  };

  // Focus a button by index
  const focusGapButton = async (index) => {
    focusedGapIndex.value = index;
    await nextTick();
    if (gapButtonRefs.value[index]) {
      gapButtonRefs.value[index].$el?.focus();
    }
  };

  // Handle keyboard navigation for roving tabindex
  const handleGapButtonKeydown = async (event) => {
    const currentIndex = focusedGapIndex.value;
    const totalButtons = gaps.length;
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        // Move to next button, wrap to first if at end
        newIndex = (currentIndex + 1) % totalButtons;
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        // Move to previous button, wrap to last if at beginning
        newIndex = (currentIndex - 1 + totalButtons) % totalButtons;
        break;

      case 'Home':
        event.preventDefault();
        // Move to first button
        newIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        // Move to last button
        newIndex = totalButtons - 1;
        break;

      case 'Enter':
      case ' ':
        // Space or Enter selects the focused button
        event.preventDefault();
        setGap(gaps[currentIndex]);
        break;

      default:
        // No action for other keys
        return;
    }

    // Focus and activate the new button if index changed
    if (newIndex !== currentIndex) {
      await focusGapButton(newIndex);
      // Also activate (select) the newly focused button
      setGap(gaps[newIndex]);
    }
  };

  // Initialize focused index to the selected gap on mount
  focusedGapIndex.value = getSelectedGapIndex();
</script>
<style scoped lang="less">
  .axis-outline {
    --axis-size: var(--dt-size-200);
    --axis-offset: var(--dt-space-200-negative);
    --axis-color: var(--dt-color-border-critical);
    --axis-opacity: var(--dt-opacity-800);
    --axis-pattern-vertical: repeating-linear-gradient(
      to bottom,
      var(--axis-color) 0,
      var(--axis-color) calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 4)
    );
    --axis-pattern-horizontal: repeating-linear-gradient(
      to right,
      var(--axis-color) 0,
      var(--axis-color) calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 2),
      transparent calc(var(--axis-size) * 4)
    );

    position: relative;

    &:before,
    &:after {
      content: "";
      position: absolute;
      z-index: 1;
      border-radius: var(--dt-size-radius-pill);
      background-image: var(--axis-pattern-vertical); // Default to vertical
      opacity: var(--axis-opacity);
    }

    &:hover:before,
    &:hover:after {
      --axis-opacity: var(--dt-opacity-1300);
      background-image: none;
      background-color: var(--axis-color);
    }

    // Inline axis indicators (vertical lines)
    &--inline-start::before {
      left: var(--axis-offset);
      width: var(--axis-size);
      height: 100%;
    }

    &--inline-center::before {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: var(--axis-size);
      height: 100%;
    }

    &--inline-end::before {
      right: var(--axis-offset);
      width: var(--axis-size);
      height: 100%;
    }

    &--inline-stretch {
      &::before {
        left: var(--axis-offset);
        width: var(--axis-size);
        height: 100%;
      }

      &::after {
        right: var(--axis-offset);
        width: var(--axis-size);
        height: 100%;
      }
    }

    // Block axis indicators (horizontal lines)
    &--block-start::before {
      top: var(--axis-offset);
      height: var(--axis-size);
      width: 100%;
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-center::before {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: var(--axis-size);
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-end::before {
      bottom: var(--axis-offset);
      height: var(--axis-size);
      width: 100%;
      background-image: var(--axis-pattern-horizontal);
    }

    &--block-stretch {
      &::before {
        top: var(--axis-offset);
        height: var(--axis-size);
        width: 100%;
        background-image: var(--axis-pattern-horizontal);
      }

      &::after {
        bottom: var(--axis-offset);
        height: var(--axis-size);
        width: 100%;
        background-image: var(--axis-pattern-horizontal);
      }
    }

    // Special case for baseline
    &--baseline::before {
      bottom: calc(var(--dt-space-500) + var(--dt-space-400));
      height: var(--axis-size);
      width: 100%;
      background-image: var(--axis-pattern-horizontal);
    }
  }
</style>